<template>
  <div>
    <div class="bg-blue-900 p-4 lg:p-12">
      <div class="max-w-5xl mx-auto mb-12">
        <div class="flex flex-col md:flex-row md:items-center">
          <h3 class="text-white font-bold">New Invoice</h3>
        </div>
      </div>
    </div>
    <div class="px-4 pb-4 lg:px-12 lg:pb-12 -mt-12">
      <div class="max-w-5xl mx-auto">
        <div class="space-y-16">
          <div class="rounded bg-white shadow-lg p-12">
            <InvoiceForm
              :default-data="defaultData"
              :recipient-options="recipientOptions"
              :issuer-options="issuerOptions"
              :server-errors="serverErrors"
              @invoice-form:submit="doStore"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { accountInvoicesClient } from '@/api/clients';
import InvoiceFormVue from '@/components/Invoices/InvoiceForm.vue';
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';

const STATUS_IDLE = 'idle';
const STATUS_PENDING = 'pending';
const STATUS_RESOLVED = 'resolved';
const STATUS_REJECTED = 'rejected';

export default {
  components: {
    InvoiceForm: InvoiceFormVue,
  },

  props: {
    accountId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      status: STATUS_IDLE,
      serverErrors: {},
      errorMessage: '',
      defaultData: {
        recipientName: '',
        recipientAddress: '',
        recipientVatNumber: '',
        recipientVatOffice: '',
      },
      recipientOptions: [],
      issuerOptions: [],
    };
  },

  computed: {
    isLoading() {
      return this.status === STATUS_PENDING;
    },
    isResolved() {
      return this.status === STATUS_RESOLVED;
    },
    isRejected() {
      return this.status === STATUS_REJECTED;
    },
  },

  async beforeRouteEnter(to, from, next) {
    try {
      const response = await accountInvoicesClient.create(to.params.accountId);

      if (!response) next(false);
      if (response.status !== 200) next(false);
      if (response?.headers['content-type'] !== 'application/json') next(false);

      next((vm) => {
        vm.setData(response.data);
      });
    } catch (error) {
      next(false);
    }
  },

  methods: {
    ...mapActions('ui/accountInvoices', {
      storeAccountInvoice: 'store',
    }),

    setData({ data, issuers }) {
      this.recipientOptions.push({
        label: data.name,
        value: data.id,
        registrations: data.registrations || [],
      });

      if (data.registrations.length) {
        const details = data.registrations[0];
        this.defaultData.recipientName = details.businessName;
        this.defaultData.recipientAddress = details.businessAddress;
        this.defaultData.recipientVatNumber = details.number;
        this.defaultData.recipientVatOffice = details.office;
      }

      this.issuerOptions = issuers.map((issuer) => ({
        label: issuer.name,
        value: issuer.id,
        registrations: issuer.registrations || [],
      }));
    },

    async doStore(formData) {
      if (this.isLoading) return;

      const { accountId: accountUuid } = this;

      this.errorMessage = '';
      this.status = STATUS_PENDING;

      try {
        await this.storeAccountInvoice({ accountUuid, formData });

        this.status = STATUS_RESOLVED;

        this.$router.replace({
          name: 'accounts.accountId.invoices',
          params: {
            accountId: this.accountId,
          },
        });
      } catch (error) {
        this.status = STATUS_REJECTED;

        if (!error.response) {
          this.errorMessage = 'Something went wrong. Try again later';
        } else if (error.response.status === HTTP_UNPROCESSABLE_ENTITY) {
          this.serverErrors = error.response.data.errors;
          this.errorMessage = error.response.data.message;
        }
      }
    },
  },
};
</script>
